import { useState, useContext, useEffect } from "react";
import { Table, Input, DatePicker, Select, Row, Col, Button, Tooltip, Form, Dropdown, Checkbox } from "antd";
import { FileExcelOutlined, ColumnHeightOutlined, CloseCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { AuthContext } from "../context/AuthContext";
import dayjs from "dayjs";
import { Image } from "antd";

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function TeamAttendance({ isHomePage }) {
    const { authData, backUrl } = useContext(AuthContext);

    const [members, setMembers] = useState([]);
    const [normalizedMembers, setNormalizedMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designation, setDesignations] = useState([]);


    const [filters, setFilters] = useState({
        name: "",
        empCode: "",
        status: "",
        organisation: "",
        department: "",
        designation: "",
        fromDate: null,
        toDate: null,
    });

    // Define all available columns
    const allColumns = [
        { key: "EmployeeCode", title: "Employee Code", defaultVisible: false },
        { key: "EmployeeName", title: "Employee Name", defaultVisible: true },
        { key: "Organisation", title: "Organisation", defaultVisible: false },
        { key: "Department", title: "Department", defaultVisible: false },
        { key: "Designation", title: "Designation", defaultVisible: false },
        { key: "Date", title: "Date", defaultVisible: true },
        { key: "AttendanceStatus", title: "Status", defaultVisible: true },
        { key: "site_location", title: "Work Location", defaultVisible: false },
        { key: "PunchIn", title: "Punch In", defaultVisible: true },
        { key: "CheckIn", title: "Check In", defaultVisible: true },
        { key: "check_in_photo_url", title: "Check In Photo", defaultVisible: true },
        { key: "CheckInLocation", title: "Check In Location", defaultVisible: true },
        { key: "CheckInWorkSite", title: "Check In Site", defaultVisible: true },
        { key: "PunchOut", title: "Punch Out", defaultVisible: true },
        { key: "CheckOut", title: "Check Out", defaultVisible: true },
        { key: "check_out_photo_url", title: "Check Out Photo", defaultVisible: true },
        { key: "CheckOutLocation", title: "Check Out Location", defaultVisible: true },
        { key: "CheckOutWorkSite", title: "Check Out Site", defaultVisible: true },
        { key: "FirstInType", title: "First In", defaultVisible: true },
        { key: "LastOutType", title: "Last Out", defaultVisible: true },
    ];

    // State for visible columns
    const [visibleColumns, setVisibleColumns] = useState(
        allColumns.filter(col => col.defaultVisible).map(col => col.key)
    );

    // State for dropdown visibility
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results);
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    const fetchDepartments = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results || []);
    };

    const fetchDesignations = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/designations/?department=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDesignations(res.data.results || []);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/hr/attendance/hod/?is_employee_view=true`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            const raw = res.data.results || [];

            const cleaned = raw.map((item) => ({
                EmployeeCode: item.EmployeeCode || item.employee_code || item.employee?.emp_code || "",
                EmployeeName:
                    item.EmployeeName ||
                    item.employee_name ||
                    `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.trim(),
                Organisation: item.Organisation || item.organisation || "",
                Department: item.Department || item.department || "",
                Designation: item.Designation || item.designation || "",
                Date: item.Date || item.date || "",
                PunchIn: item.PunchIn || item.punch_in || "",
                PunchOut: item.PunchOut || item.punch_out || "",
                CheckIn: item.CheckIn || item.check_in || "",
                CheckInIp: item.CheckInIp || item.check_in_ip || "",
                WorkLocation: item.site_location || "",
                AttendanceStatus: item.AttendanceStatus || item.attendance_status || "",
                site_location: item.site_location || "",
                check_in_photo_url: item.check_in_photo_url || "",
                CheckInLocation: item.CheckInLocation || "",
                CheckInWorkSite: item.CheckInWorkSite || "",
                CheckOut: item.CheckOut || "",
                check_out_photo_url: item.check_out_photo_url || "",
                CheckOutLocation: item.CheckOutLocation || "",
                CheckOutWorkSite: item.CheckOutWorkSite || "",
                FirstInType: item.FirstInType || "",
                LastOutType: item.LastOutType || "",
                FirstInLocation: item.FirstInLocation || "",
                FirstInTime: item.FirstInTime || "",
                LastOutTime: item.LastOutTime || "",
                PunchLocation: item.PunchLocation || "",
                StatusDB: item.StatusDB || "",
                TotalTime: item.TotalTime || "",
                check_in_latitude: item.check_in_latitude || null,
                check_in_longitude: item.check_in_longitude || null,
                check_out_latitude: item.check_out_latitude || null,
                check_out_longitude: item.check_out_longitude || null,
                OndutyTypeCheckIn: item.OndutyTypeCheckIn || null,
                OndutyTypeCheckOut: item.OndutyTypeCheckOut || null,
                OndutyWorkStatusCheckIn: item.OndutyWorkStatusCheckIn || null,
                OndutyWorkStatusCheckOut: item.OndutyWorkStatusCheckOut || null,
                consideration_requests: item.consideration_requests || [],
                is_special: item.is_special || false,
                is_special_reason: item.is_special_reason || null,
                new_end_time: item.new_end_time || null,
                new_start_time: item.new_start_time || null,
                CheckInIp: item.CheckInIp || "",
                CheckOutIp: item.CheckOutIp || "",
            }));

            setMembers(raw);
            setNormalizedMembers(cleaned);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.employee?.id) fetchData();
    }, [authData]);

    const handleOrganisationChange = (orgId) => {
        const selectedOrg = Organisations.find((item) => item.id === orgId);
        handleFilterChange("organisation", selectedOrg ? selectedOrg.name : "");
        handleFilterChange("department", "");
        handleFilterChange("designation", "");

        if (orgId) {
            fetchDepartments(orgId);
        } else {
            setDepartments([]);
            setDesignations([]);
        }
    };

    const handleRangeChange = (dates) => {
        if (!dates) {
            handleFilterChange("fromDate", null);
            handleFilterChange("toDate", null);
        } else {
            handleFilterChange("fromDate", dates[0]);
            handleFilterChange("toDate", dates[1]);
        }
    };

    const filteredMembers = normalizedMembers.filter((item) => {
        if (filters.name && !item.EmployeeName.toLowerCase().includes(filters.name.toLowerCase()))
            return false;

        if (filters.empCode && !item.EmployeeCode.toLowerCase().includes(filters.empCode.toLowerCase()))
            return false;

        if (filters.status && String(item.AttendanceStatus).toLowerCase() !== filters.status.toLowerCase())
            return false;

        if (filters.organisation && String(item.Organisation) !== String(filters.organisation))
            return false;

        if (filters.department && String(item.Department) !== String(filters.department))
            return false;

        if (filters.designation && String(item.Designation) !== String(filters.designation))
            return false;

        if (filters.fromDate && filters.toDate) {
            const itemDate = dayjs(item.Date);
            if (!itemDate.isValid()) return false;

            if (!itemDate.isBetween(filters.fromDate, filters.toDate, "day", "[]"))
                return false;
        }

        return true;
    });

    const rowSelection = {
        selectedRowKeys,
        onChange: (newKeys) => setSelectedRowKeys(newKeys),
    };

    const handleDesignationChange = (desigId) => {
        const selectedDesig = designation.find((item) => item.id === desigId);
        handleFilterChange("designation", selectedDesig ? selectedDesig.name : "");
    };

    const handleDepartmentChange = (deptId) => {
        const selectedDept = departments.find((item) => item.id === deptId);
        handleFilterChange("department", selectedDept ? selectedDept.name : "");
        fetchDesignations(deptId);
    };

    const handleDownloadExcel = () => {
        const exportData =
            selectedRowKeys.length > 0
                ? filteredMembers.filter((item) =>
                    selectedRowKeys.includes(item.EmployeeCode)
                )
                : filteredMembers;

        if (exportData.length === 0) return alert("No data to export!");

        const statusColorMap = {
            present: { fgColor: { rgb: "C6EFCE" } },        // Green
            absent: { fgColor: { rgb: "FFC7CE" } },          // Red
            weekoff: { fgColor: { rgb: "FFF2CC" } },         // Blue
            permission: { fgColor: { rgb: "E2EFDA" } },      // Yellow
            halfday_leave: { fgColor: { rgb: "EAD1DC" } },   // Orange
            sick: { fgColor: { rgb: "DDEBF7" } },            // Light Green
            casual: { fgColor: { rgb: "EAD1DC" } },          // Pink
        };

        const formattedData = exportData.map((item) => {
            const row = {};
            allColumns.forEach(col => {
                if (visibleColumns.includes(col.key)) {
                    let value = item[col.key];
                    if (col.key === "Date" && value) {
                        value = dayjs(value).format("DD/MM/YYYY");
                    }
                    row[col.title] = value || "–";
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);

        const headers = Object.keys(formattedData[0] || {});
        const statusColIndex = headers.indexOf("Status");

        // Style header row
        headers.forEach((_, colIndex) => {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
            if (!worksheet[cellAddress]) return;
            worksheet[cellAddress].s = {
                fill: { patternType: "solid", fgColor: { rgb: "0E497B" } },
                font: { bold: true, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } },
                },
            };
        });

        // Style data rows
        formattedData.forEach((row, rowIndex) => {
            const statusValue = row["Status"];
            const statusKey = statusValue?.toLowerCase().trim();
            const fillColor = statusColorMap[statusKey]?.fgColor || { rgb: "FFFFFF" };

            headers.forEach((_, colIndex) => {
                const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
                if (!worksheet[cellAddress]) {
                    worksheet[cellAddress] = { v: "", t: "s" };
                }
                worksheet[cellAddress].s = {
                    fill: { patternType: "solid", fgColor: fillColor },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "CCCCCC" } },
                        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                        left: { style: "thin", color: { rgb: "CCCCCC" } },
                        right: { style: "thin", color: { rgb: "CCCCCC" } },
                    },
                };
            });
        });

        // Auto column width
        const colWidths = headers.map((header) => {
            const maxLen = Math.max(
                header.length,
                ...formattedData.map((row) => String(row[header] || "").length)
            );
            return { wch: Math.min(maxLen + 4, 40) };
        });
        worksheet["!cols"] = colWidths;

        const workbook = XLSX.utils.book_new();

        // Required for styles to work with xlsx
        workbook.Workbook = { Views: [{ RTL: false }] };

        XLSX.utils.book_append_sheet(workbook, worksheet, "Team Attendance");
        XLSX.writeFile(workbook, "TeamAttendance.xlsx", { cellStyles: true });
    };

    // Handle column visibility toggle
    const handleColumnVisibilityChange = (columnKey, e) => {
        e.stopPropagation(); // Prevent dropdown from closing
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    // Select/Deselect all columns
    const handleSelectAllColumns = (e) => {
        e.stopPropagation(); // Prevent dropdown from closing
        setVisibleColumns(allColumns.map(col => col.key));
    };

    const handleDeselectAllColumns = (e) => {
        e.stopPropagation(); // Prevent dropdown from closing
        setVisibleColumns([]);
    };

    // Column selector dropdown menu
    const columnSelectorMenu = {
        items: [
            {
                key: 'actions',
                label: (
                    <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                        <Button
                            size="small"
                            type="link"
                            onClick={handleSelectAllColumns}
                            style={{ marginRight: 5, color: '#0e497b' }}
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            type="link"
                            onClick={handleDeselectAllColumns}
                            style={{ color: '#0e497b' }}
                        >
                            Deselect All
                        </Button>
                        <Button
                            size="small"
                            icon={<CloseCircleOutlined />}
                            type="primary"
                            onClick={() => setDropdownOpen(false)}
                            style={{ float: 'right', backgroundColor: 'white', color: '#0e497b' }}
                        >
                        </Button>
                    </div>
                ),
            },
            ...allColumns.map(col => ({
                key: col.key,
                label: (
                    <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => handleColumnVisibilityChange(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {col.title}
                    </Checkbox>
                ),
            })),
        ],
    };

    // Generate table columns based on visible columns
    const getTableColumns = () => {
        const columnDefinitions = {
            EmployeeCode: {
                title: "Employee Code",
                dataIndex: "EmployeeCode",
                render: (v) => v || "–"
            },
            EmployeeName: {
                title: "Employee Name",
                dataIndex: "EmployeeName",
                render: (v) => v || "–"
            },
            Organisation: {
                title: "Organisation",
                dataIndex: "Organisation",
                render: (v) => v || "–"
            },
            Department: {
                title: "Department",
                dataIndex: "Department",
                render: (v) => v || "–"
            },
            Designation: {
                title: "Designation",
                dataIndex: "Designation",
                render: (v) => v || "–"
            },
            Date: {
                title: "Date",
                dataIndex: "Date",
                render: (d) => d ? dayjs(d).format("DD/MM/YYYY") : "–",
            },
            AttendanceStatus: {
                title: "Status",
                dataIndex: "AttendanceStatus",
                render: (v) => v || "–"
            },
            site_location: {
                title: "Work Location",
                dataIndex: "site_location",
                render: (v) => v || "–"
            },
            PunchIn: {
                title: "Punch In",
                dataIndex: "PunchIn",
                render: (v) => v || "–"
            },
            CheckIn: {
                title: "Check In",
                dataIndex: "CheckIn",
                render: (v) => v || "–"
            },
            check_in_photo_url: {
                title: "Check In Photo",
                dataIndex: "check_in_photo_url",
                key: "check_in_photo",
                render: (url) =>
                    url ? (
                        <Image
                            width={80}
                            height={80}
                            src={url}
                            style={{ objectFit: "cover", borderRadius: 6 }}
                            preview={{ mask: "Preview" }}
                        />
                    ) : (
                        "–"
                    ),
            },
            CheckInLocation: {
                title: "Check-In Location",
                dataIndex: "CheckInLocation",
                key: "CheckInLocation",
                render: (text, record) =>
                    record.check_in_latitude && record.check_in_longitude ? (
                        <a
                            href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1677ff" }}
                        >
                            {text || "View Location"}
                        </a>
                    ) : (
                        text || "-"
                    ),
            },
            CheckInWorkSite: {
                title: "Check In Site",
                dataIndex: "CheckInWorkSite",
                render: (v, record) => (
                    <>
                        {v || "-"}
                        {record.OndutyTypeCheckIn && (
                            <> ({record.OndutyTypeCheckIn})</>
                        )}
                    </>
                ),
            },
            PunchOut: {
                title: "Punch Out",
                dataIndex: "PunchOut",
                render: (v) => v || "–"
            },
            CheckOut: {
                title: "Check Out",
                dataIndex: "CheckOut",
                render: (v) => v || "–"
            },
            check_out_photo_url: {
                title: "Check Out Photo",
                dataIndex: "check_out_photo_url",
                key: "check_out_photo",
                render: (url) =>
                    url ? (
                        <Image
                            width={80}
                            height={80}
                            src={url}
                            style={{ objectFit: "cover", borderRadius: 6 }}
                            preview={{ mask: "Preview" }}
                        />
                    ) : (
                        "–"
                    ),
            },
            CheckOutLocation: {
                title: "Check-Out Location",
                dataIndex: "CheckOutLocation",
                key: "CheckOutLocation",
                render: (text, record) =>
                    record.check_out_latitude && record.check_out_longitude ? (
                        <a
                            href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1677ff" }}
                        >
                            {text || "View Location"}
                        </a>
                    ) : (
                        text || "-"
                    ),
            },
            CheckOutWorkSite: {
                title: "Check Out Site",
                dataIndex: "CheckOutWorkSite",
                render: (v, record) => (
                    <>
                        {v || "-"}
                        {record.OndutyTypeCheckOut && (
                            <> ({record.OndutyTypeCheckOut})</>
                        )}
                    </>
                ),
            },
            FirstInType: {
                title: "First In",
                dataIndex: "FirstInType",
                render: (v) => v || "–"
            },
            LastOutType: {
                title: "Last Out",
                dataIndex: "LastOutType",
                render: (v) => v || "–"
            },

        };

        return visibleColumns
            .filter(key => columnDefinitions[key])
            .map(key => columnDefinitions[key]);
    };

    return (
        <>
            {!isHomePage && (
                <center>
                    <h5 style={{ color: "#0e497b" }}>Team Attendance</h5>
                </center>
            )}

            <div style={{ background: "#fff", padding: 10, borderRadius: 8, marginBottom: 15 }}>
                <Row gutter={[10, 10]}>
                    {!isHomePage && (
                        <>
                            <Col xs={24} sm={12} md={6} lg={4}>
                                <center>Employee Name</center>
                                <Input
                                    placeholder="Search Employee Name"
                                    value={filters.name}
                                    onChange={(e) => handleFilterChange("name", e.target.value)}
                                    allowClear
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={4}>
                                <center>Employee Code</center>
                                <Input
                                    placeholder="Search Employee Code"
                                    value={filters.empCode}
                                    onChange={(e) => handleFilterChange("empCode", e.target.value)}
                                    allowClear
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={4}>
                                <center>Status</center>
                                <Select
                                    placeholder="Select Attendance Status"
                                    value={filters.status}
                                    onChange={(val) => handleFilterChange("status", val)}
                                    allowClear
                                    style={{ width: "100%" }}
                                >
                                    <Option value="present">Present</Option>
                                    <Option value="WEEKOFF">Comp off</Option>
                                    <Option value="absent">Absent</Option>
                                    <Option value="PERMISSION">Permission</Option>
                                    <Option value="HALFDAY_LEAVE">Halfday</Option>
                                    <Option value="HALFDAY_LEAVE">Halfday</Option>
                                    <Option value="Sick">Sick Leave</Option>
                                    <Option value="Casual">Casual Leave</Option>
                                </Select>
                            </Col>
                        </>
                    )}

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <center>Organisation</center>
                        <Select
                            placeholder="Select Organisation"
                            value={filters.organisation}
                            onChange={handleOrganisationChange}
                            allowClear
                            style={{ width: "100%" }}
                        >
                            {Organisations.map((o) => (
                                <Option key={o.id} value={o.id}>
                                    {o.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <center>Department</center>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder="Select Department"
                            value={filters.department}
                            onChange={handleDepartmentChange}
                            allowClear
                            disabled={!filters.organisation}
                            style={{ width: "100%" }}
                        >
                            {departments.map((d) => (
                                <Option key={d.id} value={d.id}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <center>Designation</center>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder="Select Designation"
                            value={filters.designation}
                            onChange={handleDesignationChange}
                            allowClear
                            disabled={!filters.organisation}
                            style={{ width: "100%" }}
                        >
                            {designation.map((ds) => (
                                <Option key={ds.id} value={ds.id}>
                                    {ds.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col>
                        <RangePicker
                            placeholder={["From Date", "To Date"]}
                            value={[filters.fromDate, filters.toDate]}
                            format="DD/MM/YYYY"
                            onChange={handleRangeChange}
                            allowClear
                        />
                    </Col>

                    <Col>
                        <Tooltip title="Download Excel">
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={handleDownloadExcel}
                                style={{ backgroundColor: "#107c41", borderColor: "#107c41" }}
                            >
                                Excel
                            </Button>
                        </Tooltip>
                    </Col>

                </Row>
                <Row justify="end">
                    <Col>
                        <Dropdown
                            menu={columnSelectorMenu}
                            trigger={['click']}
                            placement="bottomRight"
                            open={dropdownOpen}
                            onOpenChange={setDropdownOpen}
                        >
                            <Tooltip title="Select Columns">
                                <Button icon={<ColumnHeightOutlined />}>
                                    Custom Columns
                                </Button>
                            </Tooltip>
                        </Dropdown>
                    </Col>
                </Row>
            </div>

            {!isHomePage && (
                <Table
                    rowSelection={rowSelection}
                    dataSource={filteredMembers}
                    columns={getTableColumns()}
                    rowKey={(record, index) => index}
                    loading={loading}
                    pagination={{ pageSize: 30 }}
                    scroll={{ x: "max-content" }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white", marginTop: "20px" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            )}
        </>
    );
}